<template>
  <div class="fts-query-designer">
    <div class="query-designer-container">
      <draggable
        class="query-designer-row"
        v-model="filter.items"
        :group="{ name: 'query' }"
        v-bind="dragOptions"
        draggable=".draggable-item"
        handle=".handle"
      >
        <template v-for="filter in filter.items">
          <div class="query-designer-row-wrapper draggable-item" :key="filter.id">
            <fts-query
              :modules="modules"
              :states="getStates"
              :information-source="informationSource"
              :variables="variables"
              :filter="filter"
              :mode="mode"
              :run-day="runDay"
              @update="updateFilter"
              @remove="removeFilter(filter)"
            >
              <template #header>
                <font-awesome-icon
                  :icon="['fas', 'grip-vertical']"
                  fixed-width
                  class="query-designer-handle handle"
                />
              </template>
            </fts-query>
          </div>
        </template>

        <div
          class="query-designer-new"
          slot="footer"
          :class="{ 'query-designer-new-visibility': queryAddVisibility }"
          @click="addFilter"
        >
          <div>
            <font-awesome-icon :icon="['fas', 'plus']" fixed-width />new condition
          </div>
        </div>
      </draggable>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import draggable from "vuedraggable";

import commons from "../commons/commons";
import queryLogic from '../commons/queryLogic';

import FTSQuery from "../elements/FTSQuery";

export default {
  props: {
    modules: Array,
    states: Array,
    informationSource: Array,
    variables: Array,
    filter: Object,
    mode: String,
    allowAnyState: {
      type: Boolean,
      default: true,
    },
    // schedule mode: the run day relative dates resolve against in each row's label
    runDay: { type: [String, Date, Object], default: null },
  },
  components: {
    draggable,
  },
  data() {
    return {
      dragOptions: commons.dragOptions
    };
  },
  methods: {
    removeFilter(filter) {
      let ind = this.filter.items.indexOf(filter);
      this.filter.items.splice(ind, 1);
    },
    addFilter() {
      if (Array.isArray(this.informationSource) && this.informationSource.length) {
        const scheduleMode = this.mode === queryLogic.SCHEDULE_MODE;
        /* a schedule's first question is usually "which state": start there when it exists */
        const firstId = scheduleMode && this.informationSource.some(x => x.id === "state")
          ? "state"
          : this.informationSource[0].id;
        this.filter.items.push({
          id: commons.nanoID(),
          ...queryLogic.prepareQueryUnit(
            this.informationSource,
            firstId,
            {},
            scheduleMode ? this.mode : undefined,
          ),
        });
      }
    },
    updateFilter() {
      const items = _.cloneDeep(this.filter.items);
      this.$set(this.filter, "items", items);
    },
  },
  computed: {
    queryAddVisibility() {
      return this.filter.items && this.filter.items.length > 0;
    },
    getStates() {
      return this.allowAnyState ? 
        [{ id: null, name: 'any'}].concat(this.states): 
        this.states;
    },
  },
  beforeCreate: function() {
    Vue.component("fts-query", FTSQuery);
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

.fts-query-designer {
  max-width: 100%;
  display: flex;
  flex-wrap: wrap;
  // Query Designer Container
  .query-designer-container {
    min-width: 0;
    flex: 1;
    &:hover {
      .query-designer-row {
        .query-designer-new.query-designer-new-visibility {
          visibility: visible;
        }
      }
    }

    // Query Designer Row
    .query-designer-row {
      position: relative;
      flex: 1;
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      margin: -0.175rem;
      // Query Designer Row Wrapper
      .query-designer-row-wrapper {
        min-width: 0;
        margin: 0.175rem;
        .handle {
          color: var(--fts-default);
          font-size: 0.75rem;
          &:hover {
            cursor: grab;
          }
        }
      }
      // Query Designer Type
      .query-designer-type {
        @include user-select-none;
        position: absolute;
        top: -1.3rem;
        left: -0.75rem;
        min-width: 55px;
        margin: 0.375rem;
        padding: 0.175rem 0.375rem;
        border-radius: 3px;
        background: var(--fts-primary);
        color: #fff;
        font-size: 0.75rem;
        text-align: center;
        line-height: 1.5;
        letter-spacing: 0.089em;
        &:hover {
          box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16),
            0 2px 10px 0 rgba(0, 0, 0, 0.12);
          cursor: pointer;
        }
      }
      // Query Designer Dropzone
      .query-designer-new {
        @include user-select-none;
        margin: 0.175rem 0.175rem 0.175rem 0.375rem;
        padding-top: 0.175rem;
        padding-bottom: 0.175rem;
        border: 1px solid transparent;
        font-size: 0.75rem;
        color: var(--fts-default);
        > div {
          @include flex-center-middle;
          height: $form-control-height;
        }
        &:hover {
          color: var(--fts-primary);
          cursor: pointer;
        }
        &.query-designer-new-visibility {
          visibility: hidden;
        }
        svg {
          margin-right: 0.275rem;
        }
      }
    }
  }
}
</style>
